import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, CircularProgress, Box, Typography,
} from "@mui/material";
import { verifyWhatsAppClient, checkWebhookStatus, getWabaOwnerInfo } from "../api/adminApi";

const ReplaceWABADialog = ({ open, client, onClose, onSuccess }) => {
  const [form, setForm] = useState({ phoneNumberId: "", wabaId: "", clientAccessToken: "", facebookBusinessManagerId: "" });
  const [reason, setReason] = useState("");
  const [webhookCheck, setWebhookCheck] = useState(null);
  const [webhookChecking, setWebhookChecking] = useState(false);
  const [ownerInfo, setOwnerInfo] = useState(null);
  const [fetchingOwner, setFetchingOwner] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const handleClose = () => {
    setForm({ phoneNumberId: "", wabaId: "", clientAccessToken: "", facebookBusinessManagerId: "" });
    setReason("");
    setWebhookCheck(null);
    setWebhookChecking(false);
    setOwnerInfo(null);
    setFetchingOwner(false);
    setSubmitting(false);
    onClose();
  };

  const handleWebhookCheck = async () => {
    if (!form.wabaId.trim()) {
      setSnack({ open: true, message: "Enter WABA ID first", severity: "warning" });
      return;
    }
    setWebhookChecking(true);
    setWebhookCheck(null);
    try {
      const res = await checkWebhookStatus(form.wabaId.trim(), form.clientAccessToken.trim());
      const apps = res.subscribedApps || [];
      setWebhookCheck({
        ok: apps.length > 0,
        message: apps.length > 0
          ? `Webhook subscribed (${apps.length} app(s) registered on this WABA): ${apps.map(a => a.name || a.app || "app").join(", ")}`
          : "Webhook NOT subscribed. Messages will not arrive until this WABA subscribes the app. Re-activate after fixing.",
      });
    } catch (err) {
      setWebhookCheck({ ok: false, message: err.message });
    }
    setWebhookChecking(false);
  };

  const handleFetchOwner = async () => {
    if (!form.wabaId.trim()) {
      setSnack({ open: true, message: "Enter WABA ID first", severity: "warning" });
      return;
    }
    setFetchingOwner(true);
    setOwnerInfo(null);
    try {
      const res = await getWabaOwnerInfo(client._id, form.wabaId.trim(), form.clientAccessToken.trim());
      setOwnerInfo(res);
      if (res.ownerBusinessId) {
        setForm((prev) => ({ ...prev, facebookBusinessManagerId: res.ownerBusinessId }));
      }
    } catch (err) {
      setOwnerInfo({ error: err.message });
    }
    setFetchingOwner(false);
  };

  const handleSubmit = async () => {
    if (!client?._id) return;
    setSubmitting(true);
    try {
      await verifyWhatsAppClient(client._id, { ...form, reason: reason.trim() || undefined });
      handleClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
    setSubmitting(false);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ className: "glass-card", sx: { borderRadius: "16px !important" } }}>
        <DialogTitle sx={{ color: "#1e293b", fontWeight: 700 }}>Replace WABA</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
            Current WABA ({client?.whatsapp?.wabaId || client?.whatsapp?.clientWabaId || "—"}) is blocked, banned, or unavailable (deleted from Meta / API error). Enter the NEW Phone Number ID & WABA ID below — the old WABA will be archived to history and this client will be activated with the new one.
          </Alert>
          <Typography variant="body2" sx={{ color: "#475569", mb: 2 }}>
            Enter the Phone Number ID, WABA ID (both from the same WhatsApp Manager account), and optionally the client's Business Manager system-user token.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField label="Phone Number ID" value={form.phoneNumberId} onChange={(e) => setForm({ ...form, phoneNumberId: e.target.value })} fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", color: "#1e293b", "& fieldset": { borderColor: "#e2e8f0" } }, "& .MuiInputLabel-root": { color: "#64748b" } }} />
            <TextField label="WABA ID" value={form.wabaId} onChange={(e) => setForm({ ...form, wabaId: e.target.value })} fullWidth size="small" placeholder="Same account as the number" sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", color: "#1e293b", "& fieldset": { borderColor: "#e2e8f0" } }, "& .MuiInputLabel-root": { color: "#64748b" } }} />
            <TextField label="Business Manager ID (optional)" value={form.facebookBusinessManagerId} onChange={(e) => setForm({ ...form, facebookBusinessManagerId: e.target.value })} fullWidth size="small" placeholder="Client's Facebook Business Manager ID" sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", color: "#1e293b", "& fieldset": { borderColor: "#e2e8f0" } }, "& .MuiInputLabel-root": { color: "#64748b" } }} />
            <TextField label="Client Access Token (optional)" value={form.clientAccessToken} onChange={(e) => setForm({ ...form, clientAccessToken: e.target.value })} fullWidth size="small" placeholder="System user token from client's BM" sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", color: "#1e293b", "& fieldset": { borderColor: "#e2e8f0" } }, "& .MuiInputLabel-root": { color: "#64748b" } }} />
            <TextField label="Replacement Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} fullWidth size="small" placeholder="e.g. banned, number lost, new WABA" sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", color: "#1e293b", "& fieldset": { borderColor: "#e2e8f0" } }, "& .MuiInputLabel-root": { color: "#64748b" } }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Button variant="outlined" onClick={handleFetchOwner} disabled={fetchingOwner || !form.wabaId.trim()}
                sx={{ color: "#0891b2", borderColor: "#0891b2", textTransform: "none", borderRadius: 2, minWidth: 150 }}>
                {fetchingOwner ? <CircularProgress size={16} sx={{ color: "#0891b2" }} /> : "Fetch BM Info"}
              </Button>
              <Button variant="outlined" onClick={handleWebhookCheck} disabled={webhookChecking || !form.wabaId.trim()}
                sx={{ color: "#6366f1", borderColor: "#6366f1", textTransform: "none", borderRadius: 2, minWidth: 150 }}>
                {webhookChecking ? <CircularProgress size={16} sx={{ color: "#6366f1" }} /> : "Check Webhook"}
              </Button>
            </Box>
            {ownerInfo && (
              ownerInfo.error ? (
                <Alert severity="error" sx={{ borderRadius: 2 }}>{ownerInfo.error}</Alert>
              ) : (
                <Alert severity={ownerInfo.ownerBusinessId ? "success" : "warning"} sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                    WABA "{ownerInfo.wabaName || ownerInfo.wabaId}" → BM{" "}
                    {ownerInfo.ownerBusinessId
                      ? `"${ownerInfo.ownerBusinessName || "unnamed"}" (ID: ${ownerInfo.ownerBusinessId})`
                      : "— owner not returned by Meta"}
                  </Typography>
                  {ownerInfo.phoneNumbers?.length > 0 && (
                    <Typography variant="caption" sx={{ color: "#475569", display: "block", mt: 0.5 }}>
                      Numbers in this WABA: {ownerInfo.phoneNumbers.map((p) => `${p.display_phone_number || p.id} (${p.status || "-"})`).join(", ")}
                    </Typography>
                  )}
                  {(ownerInfo.warnings || []).map((w, i) => (
                    <Typography key={i} variant="caption" sx={{ color: "#b45309", display: "block", mt: 0.5 }}>{w}</Typography>
                  ))}
                </Alert>
              )
            )}
            {webhookCheck && (
              <Alert severity={webhookCheck.ok ? "success" : "error"} sx={{ borderRadius: 2 }}>{webhookCheck.message}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleClose} sx={{ color: "#64748b", textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} variant="contained" sx={{ bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" }, textTransform: "none", borderRadius: 2 }}>
            {submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Replace & Activate"}
          </Button>
        </DialogActions>
      </Dialog>

      {snack.open && (
        <Alert severity={snack.severity} sx={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 9999, borderRadius: 2 }}
          onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      )}
    </>
  );
};

export default ReplaceWABADialog;
